import { Callout } from '@neshane/blueberry';
import { useLogin, useAuth } from '../../hooks';
import { AuthLayout } from '../AuthLayout';
import PhoneStep from './PhoneStep';
import OTPStep from './OTPStep';
import MFAStep from './MFAStep';
import ProfileStep from './ProfileStep';

/**
 * User Login Form Component
 * Manages 4-step login process: Phone → OTP → (MFA if enabled) → Profile
 * Each step is a separate component
 */
function UserLoginForm({ onLogin, loading = false, error = null }) {
    const { login } = useAuth();
    const {
        step,
        phoneNumber,
        sendOTP,
        verifyOTP,
        getPasskeyOptions,
        getDastineOptions,
        completeMfa,
        completeProfile,
        goBack,
        loading: loginLoading,
        error: loginError,
    } = useLogin();

    const isLoading = loading || loginLoading;
    const displayError = error || loginError;

    // Handle phone step submission
    const handlePhoneSubmit = async (values) => {
        const result = await sendOTP(values.phoneNumber);

        if (result.success && result.otp) {
            // Show OTP in development
            alert(`کد تایید: ${result.otp}`);
        }
    };

    // Handle OTP step submission
    const handleOTPSubmit = async (values) => {
        const result = await verifyOTP(values.code);

        if (result.success) {
            if (result.requiresMfa) {
                return;
            }

            if (!result.needsProfile) {
                await login(result.user);
                onLogin(result.user);
            }
        }
    };

    // Handle MFA step submission
    const handleMFASubmit = async ({ factor, code, passkeyResponse, challenge, challengeId, signature, certificate }) => {
        let payload;
        if (factor === 'passkey') {
            payload = { response: passkeyResponse };
        } else if (factor === 'dastine') {
            payload = { challenge, challengeId, signature, certificate };
        } else {
            payload = { code };
        }
        const result = await completeMfa(factor, payload);

        if (result.success) {
            await login(result.user);
            onLogin(result.user);
        }
    };

    // Handle profile step submission
    const handleProfileSubmit = async (values) => {
        // Convert Persian date to Gregorian format (YYYY-MM-DD)
        const convertPersianToGregorian = (persianDate) => {
            if (persianDate && persianDate.includes('/')) {
                const parts = persianDate.split('/');
                if (parts.length === 3) {
                    return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
                }
            }
            return persianDate;
        };

        const submitData = {
            ...values,
            birthDate: convertPersianToGregorian(values.birthDate),
        };

        const result = await completeProfile(submitData);

        if (result.success) {
            await login(result.user);
            onLogin(result.user);
        }
    };

    return (
        <AuthLayout
            title="خوش آمدید"
            subtitle="برای ورود یا ثبت‌نام، شماره موبایل خود را وارد کنید"
        >
            {displayError && (
                <Callout variant="alert" title={displayError} iconName="triangle-warning" />
            )}

            {/* Phone Step */}
            {step === 'phone' && (
                <PhoneStep
                    onSubmit={handlePhoneSubmit}
                    loading={isLoading}
                    error={displayError}
                />
            )}

            {/* OTP Step */}
            {step === 'otp' && (
                <OTPStep
                    onSubmit={handleOTPSubmit}
                    onBack={goBack}
                    phoneNumber={phoneNumber}
                    loading={isLoading}
                    error={displayError}
                />
            )}

            {/* MFA Step */}
            {step === 'mfa' && (
                <MFAStep
                    onSubmit={handleMFASubmit}
                    onBack={goBack}
                    getPasskeyOptions={getPasskeyOptions}
                    getDastineOptions={getDastineOptions}
                    loading={isLoading}
                />
            )}

            {/* Profile Step */}
            {step === 'profile' && (
                <ProfileStep
                    onSubmit={handleProfileSubmit}
                    loading={isLoading}
                />
            )}
        </AuthLayout>
    );
}

export default UserLoginForm;
